import * as React from 'react';
import {useMemo, useState} from 'react';
import {ThemeProvider} from '@mui/material/styles';
import {GridActionsCellItem, GridColDef, GridColumnVisibilityModel, GridRowId, GridToolbar} from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import {flexeMuiTheme} from '../../../shared/mui/default-mui-theme';
import {FlexeDataGrid} from '../../../shared/mui/flexe-data-grid/FlexeDataGrid';
import {EligibleForLoadShipment, PaginationModel} from '../LoadInterfaces';
import ShipmentStatusMap from '../../outbound-shipments/ShipmentConstants';

interface PreviewShipmentsTableProps {
  data: EligibleForLoadShipment[];
  handleDelete: (shipmentId: GridRowId) => void;
}

const DEFAULT_COLUMN_WIDTH = 100;
const DEFAULT_SHIPMENT_TABLE_PAGE_SIZE = 50;
const baseUrl = '/wh';

export function PreviewShipmentsTable(props: PreviewShipmentsTableProps) {
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: 0,
    pageSize: DEFAULT_SHIPMENT_TABLE_PAGE_SIZE
  });

  const handleDelete = (id: GridRowId) => {
    props.handleDelete(id);
  };

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'id',
        headerName: 'Shipment ID',
        type: 'string',
        minWidth: DEFAULT_COLUMN_WIDTH,
        flex: 1
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        renderCell: (params) => {
          const statusObject = ShipmentStatusMap.get(params.value);
          const className = `shipment-status-dot ${statusObject.className}`;
          return (
            <div>
              <span className={className} />
              {statusObject.text}
            </div>
          );
        },
        minWidth: DEFAULT_COLUMN_WIDTH,
        flex: 1
      },
      {
        field: 'purchase_order',
        headerName: 'Purchase Order',
        type: 'string',
        minWidth: DEFAULT_COLUMN_WIDTH,
        flex: 1
      },
      {
        field: 'order_id',
        headerName: 'Order ID',
        type: 'string',
        minWidth: DEFAULT_COLUMN_WIDTH,
        flex: 1
      },
      {
        field: 'destination_tag',
        headerName: 'Destination ID',
        type: 'string',
        minWidth: DEFAULT_COLUMN_WIDTH,
        flex: 1
      },
      {
        field: 'actions',
        type: 'actions',
        width: 50,
        getActions: (params) => [
          <GridActionsCellItem
            label={`Remove ${params.id} from Load`}
            onClick={() => handleDelete(params.id)}
            icon={<DeleteIcon />}
            showInMenu
          />
        ]
      }
    ],
    [baseUrl]
  );

  const columnVisibilityModel = useMemo<GridColumnVisibilityModel>(() => {
    // this column set is POC only and should be replaced with the actual set we want to display
    const enablePurchaseOrderColumn = props.data.some(({purchase_order}) => purchase_order);
    const enableOrderIdColumn = props.data.some(({order_id}) => order_id);
    const enableDestinationColumn = props.data.some(({destination_tag}) => destination_tag);
    return {
      purchase_order: enablePurchaseOrderColumn,
      order_id: enableOrderIdColumn,
      destination: enableDestinationColumn
    };
  }, [columns, props.data]);

  return (
    <ThemeProvider theme={flexeMuiTheme}>
      <FlexeDataGrid
        autoHeight
        slots={{toolbar: GridToolbar}}
        slotProps={{
          toolbar: {
            showQuickFilter: false
          }
        }}
        columns={columns}
        rows={props.data}
        rowCount={props.data?.length || 0}
        pageSizeOptions={[20, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        columnVisibilityModel={columnVisibilityModel}
        sx={{
          '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {py: '8px'}
        }}
        disableColumnSelector
        disableDensitySelector
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
      />
    </ThemeProvider>
  );
}
